import React, { useContext, useEffect } from 'react';
import { ButtonTheme, Placement } from '@philips-internal/oswald-ui-constants';
import { AwesomeComponent, ComponentType } from '@philips-internal/oswald-ui-core';
import { bool, element, eventListener, number, oneOf, oneOfType, string } from '@philips-internal/oswald-ui-core/lib/component/prop-types/index.js';
import { ClickEvent } from '@philips-internal/oswald-ui-core/lib/component/types/index.js';
import { EditorService } from '@philips-internal/oswald-ui-core/lib/services/index.js';
import { deprecationWarningMessage, enumValues, noop, trigger } from '@philips-internal/oswald-ui-utils';
import { ActionOption } from '../action-option/action-option.js';
import { HeadingContext, HeadingProvider } from '../heading/components/heading-context/heading-context.js';
import { ActionAPI } from './action-api.js';
import { ActionStyle } from './action-constants.js';
const awesomeProps = {
    api: ActionAPI,
    type: ComponentType.NAVIGATION,
};
const omittedStyles = [
    String(ActionStyle.OPTION),
    String(ActionStyle.OPTION_GROUP),
];
function InternalAction(props) {
    const context = useContext(HeadingContext);
    useEffect(() => {
        if (props.buttonTheme === ButtonTheme.DEFAULT) {
            deprecationWarningMessage({
                deadlineVersion: '7.0.0',
                feature: true,
                replaced: 'ButtonTheme.DEFAULT',
                scope: 'Action',
                startingVersion: '6.10.1',
            });
        }
        if (!context) {
            return;
        }
        const popListener = context.addOptionListener(() => updateContext());
        const popAction = updateContext();
        return () => {
            popAction();
            popListener();
        };
    }, []);
    useEffect(() => {
        const popAction = updateContext();
        return () => {
            popAction();
        };
    }, [props, context]);
    const updateContext = () => {
        if (!props.parentContext) {
            return noop;
        }
        const action = {
            buttonTheme: props.buttonTheme,
            className: props.className,
            enabled: props.enabled,
            icon: props.icon,
            iconPlacement: props.iconPlacement,
            id: props.uuid,
            inlineOnMobile: props.inlineOnMobile,
            onChange: props.onChange,
            onClick: trigger(props.onClick, new ClickEvent()),
            onContextMenuClick: trigger(props.onContextMenuClick, new ClickEvent()),
            onDoubleClick: trigger(props.onDoubleClick, new ClickEvent()),
            options: context?.getOptions(),
            style: props.style,
            tabIndex: props.tabIndex,
            text: props.text,
        };
        return props.parentContext.addOrUpdateAction(action);
    };
    return props.children ?? null;
}
InternalAction.propTypes = {
    ...ActionOption.propTypes,
    buttonTheme: oneOf(enumValues(ButtonTheme)),
    enabled: bool,
    icon: oneOfType([string, element]),
    iconPlacement: oneOf([Placement.LEFT, Placement.RIGHT]),
    inlineOnMobile: bool,
    onChange: eventListener,
    style: oneOf(enumValues(ActionStyle).filter(style => !omittedStyles.includes(style))),
    tabIndex: number,
};
InternalAction.defaultProps = {
    ...ActionOption.defaultProps,
    buttonTheme: ButtonTheme.PRIMARY,
    iconPlacement: Placement.LEFT,
    style: ActionStyle.BUTTON,
    text: EditorService.isEditor() ? 'Action' : '',
};
function Action(props) {
    const context = useContext(HeadingContext);
    if (!context) {
        return null;
    }
    return (React.createElement(HeadingProvider, { type: context.getType() },
        React.createElement(InternalAction, { ...props, parentContext: context })));
}
Action.propTypes = {
    ...InternalAction.propTypes,
};
Action.defaultProps = {
    ...InternalAction.defaultProps,
};
AwesomeComponent(awesomeProps)(Action);
export { Action, };
//# sourceMappingURL=action.js.map